import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeft, ArrowRight, Boxes, Building2, CalendarDays, Check, Clock,
  Loader2, Mail, MessageSquare, Moon, Package, Phone, ShieldCheck, Sun,
  TrendingUp, User, Users,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

/**
 * Formulir pengajuan demo.
 *
 * Formulir ini milik Stokku, bukan DemoGate: pengunjung tidak pernah melihat
 * DemoGate. Data dikirim ke API DemoGate di latar belakang, dan DemoGate hanya
 * menyimpan permintaan agar pemilik bisa mengirim kredensial setelah disetujui.
 */

const API_BASE = import.meta.env.VITE_DEMOGATE_API || 'https://demogate.elngdev.workers.dev';
const APP_SLUG = 'stokku';

const STEPS = ['Jadwal', 'Kontak', 'Selesai'];

const highlights = [
  { icon: Boxes, title: 'Stok real-time', text: 'Kartu stok per gudang, otomatis.' },
  { icon: TrendingUp, title: 'Jurnal otomatis', text: 'Setiap transaksi langsung terposting.' },
  { icon: ShieldCheck, title: 'Peran terbatas', text: 'Akses granular untuk tiap tim.' },
];

function formatDate(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('id-ID', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
  });
}

function formatDateShort(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('id-ID', {
    weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC',
  });
}

function weekday(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('id-ID', {
    weekday: 'short', timeZone: 'UTC',
  });
}

export default function ScheduleDemoPage() {
  const { isDark, toggleTheme } = useTheme();

  const [dates, setDates] = useState([]);
  const [date, setDate] = useState('');
  const [slots, setSlots] = useState([]);
  const [time, setTime] = useState('');
  const [duration, setDuration] = useState(45);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(0);
  const [result, setResult] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [windowStart, setWindowStart] = useState(0);

  const [form, setForm] = useState({
    name: '', org: '', email: '', phone: '', roleHint: '', headcount: 1, agenda: '',
  });

  const PAGE_DAYS = 10;
  const visibleDates = useMemo(
    () => dates.slice(windowStart, windowStart + PAGE_DAYS),
    [dates, windowStart]
  );
  const canBack = windowStart > 0;
  const canForward = windowStart + PAGE_DAYS < dates.length;

  async function loadSlots(targetDate) {
    setLoadingSlots(true);
    setTime('');
    try {
      const res = await fetch(
        `${API_BASE}/api/demo-request?app=${APP_SLUG}&date=${encodeURIComponent(targetDate)}`
      );
      const payload = await res.json();
      setSlots(payload.slots ?? []);
      if (payload.dates?.length && dates.length === 0) {
        setDates(payload.dates);
        setDate(payload.dates[0]);
      }
      if (payload.durationMinutes) setDuration(payload.durationMinutes);
    } catch {
      setSlots([]);
      toast.error('Gagal memuat jadwal. Coba lagi.');
    } finally {
      setLoadingSlots(false);
    }
  }

  // Muat tanggal dan slot pertama saat halaman dibuka.
  useEffect(() => {
    (async () => {
      setLoadingSlots(true);
      try {
        const res = await fetch(`${API_BASE}/api/demo-request?app=${APP_SLUG}&date=2000-01-01`);
        const payload = await res.json();
        const list = payload.dates ?? [];
        setDates(list);
        if (list.length) {
          setDate(list[0]);
          const res2 = await fetch(
            `${API_BASE}/api/demo-request?app=${APP_SLUG}&date=${encodeURIComponent(list[0])}`
          );
          const p2 = await res2.json();
          setSlots(p2.slots ?? []);
          if (p2.durationMinutes) setDuration(p2.durationMinutes);
        }
      } catch {
        toast.error('Gagal memuat jadwal. Periksa koneksi Anda.');
      } finally {
        setLoadingSlots(false);
      }
    })();
  }, []);

  function pickDate(value) {
    if (value === date) return;
    setDate(value);
    loadSlots(value);
  }

  function shiftWindow(dir) {
    const next = Math.min(
      Math.max(windowStart + dir * PAGE_DAYS, 0),
      Math.max(dates.length - PAGE_DAYS, 0)
    );
    setWindowStart(next);
    if (!visibleDates.includes(date) && dates[next]) pickDate(dates[next]);
  }

  function validateContact() {
    const errors = {};
    if (form.name.trim().length < 2) errors.name = 'Tulis nama lengkap Anda.';
    if (!/^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(form.email.trim())) {
      errors.email = 'Alamat email belum benar.';
    }
    if (!/^[+()\d\s-]{7,20}$/.test(form.phone.trim())) {
      errors.phone = 'Nomor telepon belum benar.';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function submit(e) {
    e.preventDefault();
    if (!time) {
      toast.error('Pilih jam demo terlebih dahulu.');
      return;
    }
    if (!validateContact()) return;

    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/demo-request`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          app: APP_SLUG,
          name: form.name.trim(),
          org: form.org.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          roleHint: form.roleHint,
          headcount: Number(form.headcount) || 1,
          agenda: form.agenda.trim(),
          date,
          time,
        }),
      });
      const payload = await res.json();

      if (!res.ok) {
        if (payload.fields) setFieldErrors(payload.fields);
        toast.error(payload.error || 'Permintaan gagal dikirim.');
        if (payload.fields?.time) loadSlots(date);
        return;
      }

      setResult(payload);
      setStep(2);
    } catch {
      toast.error('Permintaan gagal dikirim. Periksa koneksi Anda.');
    } finally {
      setSubmitting(false);
    }
  }

  const selectedSlot = slots.find((s) => s.time === time);
  const freeCount = slots.filter((s) => s.available).length;

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]">
      {/* ── Formulir ── */}
      <div className="flex min-h-screen flex-col px-6 py-8 sm:px-10 lg:px-16 xl:px-24">
        <div className="flex items-center justify-between">
          <Link to="/login" className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary-600 shadow-glow">
              <Package size={17} className="text-primary-900" />
            </div>
            <span className="text-[0.95rem] font-semibold tracking-[-0.2px] text-surface-900 dark:text-white">
              Stokku
            </span>
          </Link>
          <button
            type="button"
            onClick={toggleTheme}
            className="rounded-full p-2 text-surface-400 transition-colors hover:bg-surface-100 hover:text-surface-700 dark:hover:bg-surface-800 dark:hover:text-surface-200"
            aria-label="Ganti tema"
          >
            {isDark ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        </div>

        <div className="mx-auto flex w-full max-w-lg flex-1 flex-col justify-center py-10">
          {step < 2 && (
            <div className="animate-rise">
              <Link
                to="/login"
                className="mb-6 inline-flex items-center gap-1.5 text-xs text-surface-400 transition-colors hover:text-surface-700 dark:hover:text-surface-200"
              >
                <ArrowLeft size={13} /> Kembali ke halaman masuk
              </Link>
              <span className="chip mb-5">
                <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
                Demo produk
              </span>
              <h1 className="text-[2.25rem] leading-[1.15] tracking-[-0.5px] text-surface-900 dark:text-white">
                Jadwalkan <span className="text-primary-900 dark:text-primary-400">demo</span> Stokku
              </h1>
              <p className="mt-3 text-sm leading-relaxed text-surface-500 dark:text-surface-400">
                Pilih tanggal dan jam, lalu isi kontak. Setelah disetujui, kredensial akun demo
                dikirim ke email Anda.
              </p>

              {/* Langkah */}
              <ol className="mt-6 flex items-center gap-2 text-[11px] text-surface-400">
                {STEPS.map((label, i) => (
                  <li key={label} className="flex items-center gap-2">
                    <span
                      className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-semibold ${
                        i <= step
                          ? 'bg-primary-600 text-primary-900'
                          : 'bg-surface-100 text-surface-400 dark:bg-surface-800'
                      }`}
                    >
                      {i < step ? <Check size={11} /> : i + 1}
                    </span>
                    <span className={i === step ? 'font-medium text-surface-700 dark:text-surface-200' : ''}>
                      {label}
                    </span>
                    {i < STEPS.length - 1 && <span className="h-px w-4 bg-surface-200 dark:bg-surface-700" />}
                  </li>
                ))}
              </ol>
            </div>
          )}

          {step === 2 && result ? (
            <div className="animate-rise">
              <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/40">
                <Check size={22} className="text-primary-800 dark:text-primary-300" />
              </div>
              <h1 className="text-[1.75rem] leading-tight tracking-[-0.4px] text-surface-900 dark:text-white">
                Permintaan demo terkirim
              </h1>
              <p className="mt-3 text-sm leading-relaxed text-surface-500 dark:text-surface-400">
                Terima kasih, {result.ref ? form.name.split(' ')[0] : 'Anda'}. Permintaan Anda
                sedang ditinjau. Bila disetujui, kredensial akun demo dikirim ke{' '}
                <strong className="text-surface-700 dark:text-surface-200">{form.email}</strong>.
              </p>

              <dl className="mt-6 space-y-2.5 rounded-xl border border-surface-200 bg-surface-50/70 p-4 text-sm dark:border-surface-800 dark:bg-surface-900/50">
                <div className="flex justify-between gap-3">
                  <dt className="text-surface-500 dark:text-surface-400">Referensi</dt>
                  <dd className="font-mono font-medium text-surface-900 dark:text-white">{result.ref}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-surface-500 dark:text-surface-400">Jadwal</dt>
                  <dd className="text-right text-surface-900 dark:text-white">
                    {formatDate(result.date)} · {result.time}
                  </dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-surface-500 dark:text-surface-400">Durasi</dt>
                  <dd className="text-surface-900 dark:text-white">{result.durationMinutes} menit</dd>
                </div>
              </dl>

              <p className="mt-4 text-xs leading-relaxed text-surface-400">
                Simpan nomor referensi di atas. Kami menghubungi Anda lewat email yang Anda
                cantumkan.
              </p>

              <Link to="/login" className="btn-primary mt-6 inline-flex w-full items-center justify-center gap-2 py-2.5">
                Kembali ke halaman masuk <ArrowRight size={15} />
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-5">
              {/* Jadwal */}
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="label mb-0">Tanggal</label>
                  <span className="text-[11px] text-surface-400">{duration} menit per sesi</span>
                </div>

                <div className="flex items-stretch gap-1.5">
                  <button
                    type="button"
                    onClick={() => shiftWindow(-1)}
                    disabled={!canBack}
                    className="flex w-8 items-center justify-center rounded-lg border border-surface-200 text-surface-400 transition-colors hover:border-surface-300 hover:text-surface-700 disabled:opacity-30 dark:border-surface-700 dark:hover:text-surface-200"
                    aria-label="Tanggal sebelumnya"
                  >
                    <ArrowLeft size={14} />
                  </button>
                  <div className="grid flex-1 grid-cols-5 gap-1 sm:grid-cols-10">
                    {visibleDates.map((d) => (
                      <button
                        key={d}
                        type="button"
                        onClick={() => pickDate(d)}
                        className={`flex flex-col items-center gap-0.5 rounded-lg border px-1 py-1.5 transition-colors ${
                          date === d
                            ? 'border-primary-600 bg-primary-600 text-primary-900'
                            : 'border-surface-200 text-surface-500 hover:border-surface-300 dark:border-surface-700 dark:text-surface-400'
                        }`}
                      >
                        <span className="text-[9px] uppercase tracking-wide opacity-70">{weekday(d)}</span>
                        <span className="text-xs font-medium">{d.split('-')[2]}</span>
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => shiftWindow(1)}
                    disabled={!canForward}
                    className="flex w-8 items-center justify-center rounded-lg border border-surface-200 text-surface-400 transition-colors hover:border-surface-300 hover:text-surface-700 disabled:opacity-30 dark:border-surface-700 dark:hover:text-surface-200"
                    aria-label="Tanggal berikutnya"
                  >
                    <ArrowRight size={14} />
                  </button>
                </div>

                <p className="mt-2 flex items-center gap-1.5 text-[11px] text-surface-400">
                  <CalendarDays size={12} />
                  {formatDate(date)}
                  <span className="mx-1">·</span>
                  {loadingSlots ? 'memuat jam…' : `${freeCount} dari ${slots.length} jam tersedia`}
                </p>
              </div>

              {/* Jam */}
              <div>
                <label className="label">Jam</label>
                <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-6">
                  {slots.map((s) => (
                    <button
                      key={s.time}
                      type="button"
                      disabled={!s.available}
                      onClick={() => setTime(s.time)}
                      title={s.reason || undefined}
                      className={`rounded-lg border px-1 py-1.5 text-xs transition-colors ${
                        time === s.time
                          ? 'border-primary-600 bg-primary-600 font-medium text-primary-900'
                          : s.available
                            ? 'border-surface-200 text-surface-600 hover:border-surface-300 dark:border-surface-700 dark:text-surface-300'
                            : 'cursor-not-allowed border-dashed border-surface-200 text-surface-300 line-through dark:border-surface-800 dark:text-surface-600'
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                  {!loadingSlots && slots.length === 0 && (
                    <p className="col-span-full text-xs text-surface-400">
                      Tidak ada jam pada tanggal ini. Pilih tanggal lain.
                    </p>
                  )}
                </div>
                {fieldErrors.time && <p className="error-text">{fieldErrors.time}</p>}
              </div>

              {/* Kontak */}
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="label" htmlFor="name">Nama lengkap</label>
                  <div className="relative">
                    <User size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-surface-400" />
                    <input
                      id="name" className={`input py-2 pl-9 ${fieldErrors.name ? 'input-error' : ''}`}
                      value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Nama Anda" required
                    />
                  </div>
                  {fieldErrors.name && <p className="error-text">{fieldErrors.name}</p>}
                </div>
                <div>
                  <label className="label" htmlFor="org">Instansi</label>
                  <div className="relative">
                    <Building2 size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-surface-400" />
                    <input
                      id="org" className="input py-2 pl-9" value={form.org}
                      onChange={(e) => setForm({ ...form, org: e.target.value })}
                      placeholder="Opsional"
                    />
                  </div>
                </div>
                <div>
                  <label className="label" htmlFor="email">Email</label>
                  <div className="relative">
                    <Mail size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-surface-400" />
                    <input
                      id="email" type="email" className={`input py-2 pl-9 ${fieldErrors.email ? 'input-error' : ''}`}
                      value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="nama@instansi.id" required
                    />
                  </div>
                  {fieldErrors.email && <p className="error-text">{fieldErrors.email}</p>}
                </div>
                <div>
                  <label className="label" htmlFor="phone">WhatsApp</label>
                  <div className="relative">
                    <Phone size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-surface-400" />
                    <input
                      id="phone" type="tel" className={`input py-2 pl-9 ${fieldErrors.phone ? 'input-error' : ''}`}
                      value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="0812 3456 7890" required
                    />
                  </div>
                  {fieldErrors.phone && <p className="error-text">{fieldErrors.phone}</p>}
                </div>
                <div>
                  <label className="label" htmlFor="roleHint">Peran Anda</label>
                  <select
                    id="roleHint" className="input py-2" value={form.roleHint}
                    onChange={(e) => setForm({ ...form, roleHint: e.target.value })}
                  >
                    <option value="">Pilih peran</option>
                    <option value="owner">Pemilik / direktur</option>
                    <option value="finance">Keuangan / akuntansi</option>
                    <option value="operations">Operasional</option>
                    <option value="it">IT / sistem informasi</option>
                    <option value="other">Lainnya</option>
                  </select>
                </div>
                <div>
                  <label className="label" htmlFor="headcount">Jumlah peserta</label>
                  <div className="relative">
                    <Users size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-surface-400" />
                    <input
                      id="headcount" type="number" min="1" max="20" className="input py-2 pl-9"
                      value={form.headcount} onChange={(e) => setForm({ ...form, headcount: e.target.value })}
                    />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label className="label" htmlFor="agenda">Yang ingin Anda lihat</label>
                  <div className="relative">
                    <MessageSquare size={15} className="pointer-events-none absolute left-3 top-3 text-surface-400" />
                    <textarea
                      id="agenda" rows={3} className="input py-2 pl-9"
                      value={form.agenda} onChange={(e) => setForm({ ...form, agenda: e.target.value })}
                      placeholder="Contoh: alur pembelian sampai jurnal otomatis."
                    />
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-surface-200 bg-surface-50/70 p-3 dark:border-surface-800 dark:bg-surface-900/50">
                <p className="flex items-start gap-2 text-[11px] leading-relaxed text-surface-500 dark:text-surface-400">
                  <ShieldCheck size={13} className="mt-0.5 flex-shrink-0" />
                  Data kontak dipakai hanya untuk mengatur demo ini. Akun demo tidak boleh
                  dibagikan ke pihak lain.
                </p>
              </div>

              <div className="flex items-center justify-between gap-3 pt-1">
                <p className="text-[11px] text-surface-400">
                  {time ? (
                    <span className="inline-flex items-center gap-1.5">
                      <Clock size={12} /> {formatDateShort(date)} · {selectedSlot?.label ?? time}
                    </span>
                  ) : (
                    'Pilih jam untuk melanjutkan'
                  )}
                </p>
                <button type="submit" disabled={!time || submitting} className="btn-primary inline-flex items-center gap-2 py-2.5">
                  {submitting ? <><Loader2 size={15} className="animate-spin" /> Mengirim…</> : <>Kirim permintaan <ArrowRight size={15} /></>}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* ── Panel kanan ── */}
      <div className="relative hidden overflow-hidden border-l border-surface-200 bg-surface-50/60 lg:flex lg:flex-col lg:justify-center lg:px-14 dark:border-surface-800 dark:bg-surface-900/40">
        <div className="pointer-events-none absolute inset-0 opacity-[0.5] dark:opacity-[0.25]" aria-hidden="true">
          <div className="absolute -right-24 top-10 h-72 w-72 rounded-full bg-primary-200/60 blur-3xl dark:bg-primary-900/30" />
          <div className="absolute -left-16 bottom-16 h-64 w-64 rounded-full bg-primary-100/70 blur-3xl dark:bg-primary-950/40" />
        </div>
        <div className="relative max-w-md">
          <span className="chip mb-5">
            <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
            Demo 45 menit
          </span>
          <h2 className="text-[2.5rem] leading-[1.08] tracking-[-0.8px] text-surface-900 dark:text-white">
            Lihat stok, penjualan, dan jurnal dalam satu alur.
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-surface-500 dark:text-surface-400">
            Sesi dipandu langsung. Anda bisa meminta bagian tertentu untuk ditunjukkan lebih
            dalam, misalnya penerimaan barang atau laporan laba rugi.
          </p>
          <ul className="mt-8 space-y-3">
            {highlights.map((h) => (
              <li key={h.title} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-white text-primary-800 shadow-sm dark:bg-surface-800 dark:text-primary-300">
                  <h.icon size={14} />
                </span>
                <span>
                  <span className="block text-xs font-medium text-surface-700 dark:text-surface-200">{h.title}</span>
                  <span className="block text-[11px] text-surface-400">{h.text}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
        <p className="relative mt-12 text-[11px] text-surface-400">© 2026 Stokku</p>
      </div>
    </div>
  );
}
